/**
 * The address, as the trail of places above it.
 *
 * ## A record's own name is not in here, and that is the decision
 *
 * `/projects/3f2a.../knowledge` could read "Projects / Kano nutrition /
 * Knowledge", and it does not. The shell does not have the project's name: it
 * knows the id in the address and nothing else, and the only ways to put a name
 * here are to fetch the project a second time in the frame, or to give the
 * screen a channel back up into the chrome. The first duplicates a request the
 * page below is already making and shows a stale name for as long as it takes;
 * the second means every detail screen has to remember to push its title, and
 * the one that forgets renders a breadcrumb still naming the record somebody
 * was looking at a moment ago.
 *
 * So the trail names sections, which the shell genuinely knows, and the record
 * is named by the `PageHeader` directly underneath it, which is where the
 * screen already puts it. The two crumbs of a detail page then read
 * "Projects / Project", which is honest about being structural, and the link on
 * the first one is the thing a person actually came to the breadcrumb for.
 */

import { sectionFor, subSectionLabel } from './navigation'
import type { Crumb } from '@/components/layout'

/**
 * What a segment that is an identifier is called.
 *
 * Singular, and derived from the section rather than from the id, because "Case
 * fa31-..." in a bar at the top of the page is an id read out in full by a
 * screen reader for no benefit at all.
 */
const RECORD_LABELS: Readonly<Record<string, string>> = {
  '/projects': 'Project',
  '/cases': 'Case',
  '/broadcasts': 'Broadcast',
  '/tasks': 'Task',
}

export function breadcrumbFor(pathname: string): Crumb[] {
  const section = sectionFor(pathname)
  if (!section) {
    // An address in no section: the not-found page, or the root before its
    // redirect has run. One crumb, so the bar is never empty and never lies.
    return [{ label: 'Namu' }]
  }

  const crumbs: Crumb[] = [{ label: section.label, to: section.to }]
  const segments = pathname.split('/').filter(Boolean).slice(1)

  for (const segment of segments) {
    const known = subSectionLabel(segment)
    crumbs.push({ label: known ?? RECORD_LABELS[section.to] ?? 'Detail' })
  }

  /*
   * The last crumb is the page itself and must not be a link to where the
   * person already is. `Topbar` also refuses to render the last one as a link,
   * and the two together are belt and braces on purpose: this function is what
   * a test reads, and a trail whose final entry carries an address is wrong
   * whether or not the component that draws it happens to ignore it.
   */
  const last = crumbs[crumbs.length - 1]
  if (last) delete last.to

  return crumbs
}
